import { FormControl, MenuItem, Select } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PaymentFilters as PaymentFiltersType } from "../../types";
import { PaymentProvider } from "../PaymentProvider";
import { getBranchDisplayName } from "../../utils/branch-display-name";

const commonInputStyles = {
  "& .MuiSelect-select": {
    padding: "4px 8px",
    fontFamily: "Montserrat",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    minHeight: "unset !important",
    height: "unset !important",
    backgroundColor: "transparent",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e5e7eb",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#EF0BB8",
    borderWidth: "1px",
  },
  "& .MuiChip-root": {
    height: "20px",
    fontFamily: "Montserrat",
    fontSize: "0.75rem",
    margin: "2px",
    backgroundColor: "#f3f4f6",
  },
  "& .MuiInputBase-root": {
    padding: "0",
    height: "32px",
  },
};

const commonMenuStyles = {
  "& .MuiMenuItem-root": {
    fontFamily: "Montserrat",
    fontSize: "0.875rem",
    padding: "6px 16px",
  },
  "& .MuiPaper-root": {
    marginTop: "4px",
    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  },
};

export type FilterProps = {
  branch: string[];
  employee: string[];
  provider: string[];
  paymentStatus: string[];
  transactionStatus: string[];
  minAmount: string;
  maxAmount: string;
};

interface PaymentFiltersProps {
  onApplyFilters: (filters: FilterProps) => void;
  filterValues: PaymentFiltersType;
  resetDateFilters: () => void;
  totalTransferred?: number;
}

export function PaymentFilters({ onApplyFilters, filterValues, resetDateFilters, totalTransferred }: PaymentFiltersProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<FilterProps>({
    branch: [],
    employee: [],
    provider: [],
    paymentStatus: [],
    transactionStatus: [],
    minAmount: "",
    maxAmount: "",
  });

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelectChange = (event: SelectChangeEvent<string[]>, field: string) => {
    const value = event.target.value as string[];
    handleFilterChange(field, value);
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
  };

  const handleResetFilters = () => {
    resetDateFilters();
    setFilters({
      branch: [],
      employee: [],
      provider: [],
      paymentStatus: [],
      transactionStatus: [],
      minAmount: "",
      maxAmount: "",
    });
    onApplyFilters({
      branch: [],
      employee: [],
      provider: [],
      paymentStatus: [],
      transactionStatus: [],
      minAmount: "",
      maxAmount: "",
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div
        className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
          isExpanded ? "bg-gray-50" : ""
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="text-sm font-medium text-gray-900">
          {t("payments.filters.title")}
        </h3>
        <svg
          className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      <div
        className={`transition-all duration-300 ease-in-out ${
          isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="p-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end">
            <FormControl fullWidth>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("payments.tips.branch")}
              </label>
              <Select
                multiple
                displayEmpty
                value={filters.branch}
                onChange={(e) => handleMultiSelectChange(e, "branch")}
                className="w-full text-sm border border-gray-300 rounded-md h-8 focus:outline-none focus:ring-2 focus:ring-[#EF0BB8]"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="text-gray-500 font-montserrat block truncate">
                       {t("payments.filters.branch-placeholder")}
                      </span>
                    );
                  }
                  return selected
                    .map((id) => {
                      const branch = filterValues.branches.find(
                        (b) => b.id.toString() === id
                      );
                      return branch ? getBranchDisplayName(branch) : "";
                    })
                    .join(", ");
                }}
                sx={commonInputStyles}
                MenuProps={{
                  PaperProps: {
                    sx: commonMenuStyles,
                  },
                }}
              >
                {filterValues.branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id.toString()}>
                    {getBranchDisplayName(branch)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("payments.tips.employee")}
              </label>
              <Select
                multiple
                displayEmpty
                value={filters.employee}
                onChange={(e) => handleMultiSelectChange(e, "employee")}
                className="w-full text-sm border border-gray-300 rounded-md h-8 focus:outline-none focus:ring-2 focus:ring-[#EF0BB8]"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="text-gray-500 font-montserrat block truncate">
                        {t("payments.filters.employee-placeholder")}
                      </span>
                    );
                  }
                  return selected
                    .map(
                      (id) =>
                        filterValues.employees.find(
                          (e) => e.id.toString() === id
                        )?.name
                    )
                    .join(", ");
                }}
                sx={commonInputStyles}
                MenuProps={{
                  PaperProps: {
                    sx: commonMenuStyles,
                  },
                }}
              >
                {filterValues.employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id.toString()}>
                    {employee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("payments.tips.provider")}
              </label>
              <Select
                multiple
                displayEmpty
                value={filters.provider}
                onChange={(e) => handleMultiSelectChange(e, "provider")}
                className="w-full text-sm border border-gray-300 h-8 rounded-md focus:outline-none focus:ring-2 focus:ring-[#EF0BB8]"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="text-gray-500 font-montserrat">
                        {t("payments.filters.providers")}
                      </span>
                    );
                  }
                  return selected
                    .map(
                      (id) =>
                        filterValues.providers.find(
                          (p) => p.id.toString() === id
                        )?.name
                    )
                    .join(", ");
                }}
                sx={commonInputStyles}
                MenuProps={{
                  PaperProps: {
                    sx: commonMenuStyles,
                  },
                }}
              >
                {filterValues.providers.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id.toString()}>
                    <PaymentProvider provider={provider.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("payments.filters.payment-status")}
              </label>
              <Select
                multiple
                displayEmpty
                value={filters.paymentStatus}
                onChange={(e) => handleMultiSelectChange(e, "paymentStatus")}
                className="w-full text-sm border border-gray-300 h-8 rounded-md focus:outline-none focus:ring-2 focus:ring-[#EF0BB8]"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="text-gray-500 font-montserrat">
                        {t("payments.filters.payment-status-placeholder")}
                      </span>
                    );
                  }
                  return selected
                    .map(status => t(`payments.tips.${status.toLowerCase()}`))
                    .join(", ");
                }}
                sx={commonInputStyles}
                MenuProps={{
                  PaperProps: {
                    sx: commonMenuStyles,
                  },
                }}
              >
                {filterValues.tipStatus.map((status) => (
                  <MenuItem key={status.id} value={status.name.toLowerCase()}>
                    {t(`payments.tips.${status.name.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("payments.filters.transaction-status")}
              </label>
              <Select
                multiple
                displayEmpty
                value={filters.transactionStatus}
                onChange={(e) => handleMultiSelectChange(e, "transactionStatus")}
                className="w-full text-sm border border-gray-300 h-8 rounded-md focus:outline-none focus:ring-2 focus:ring-[#EF0BB8]"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="text-gray-500 font-montserrat">
                        {t("payments.filters.transaction-status-placeholder")}
                      </span>
                    );
                  }
                  return selected
                    .map(status => t(`payments.transactions.${status.toLowerCase()}`))
                    .join(", ");
                }}
                sx={commonInputStyles}
                MenuProps={{
                  PaperProps: {
                    sx: commonMenuStyles,
                  },
                }}
              >
                {filterValues.transactionsStatus.map((status) => (
                  <MenuItem key={status.id} value={status.name.toLowerCase()}>
                    {t(`payments.transactions.${status.name.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={handleResetFilters}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#EF0BB8]"
            >
              {t("payments.filters.reset")}
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-3 py-1.5 text-sm font-medium text-white bg-[#EF0BB8] border border-transparent rounded-md hover:bg-[#EF0BB8]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#EF0BB8]"
            >
              {t("payments.filters.apply")}
            </button>
          </div>

          {totalTransferred !== undefined && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  {t("payments.filters.total-transferred")}
                </span>
                <span className="text-lg font-bold text-[#EF0BB8]">
                  ${totalTransferred.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 